import type { Locale } from "@/i18n/types";

/**
 * Returns a concise "what this phase is for" sentence based on the day offset
 * of a protocol step. Used to explain to the user where the journey is going
 * (baseline → early effects → first measurable change → mid-review → consolidation → final).
 *
 * Per-protocol overrides can be passed as `override` to refine the wording.
 */
export function getPhaseGoal(
  dayOffset: number,
  locale: Locale,
  override?: Partial<Record<Locale, string>>,
): string {
  if (override?.[locale]) return override[locale]!;

  // Pre-op window
  if (dayOffset < 0) {
    return PICK(locale, {
      fr: "Préparation : on capture l'état de départ pour pouvoir comparer après.",
      en: "Preparation: we capture the starting state so we can compare later.",
      nl: "Voorbereiding: we leggen de uitgangssituatie vast om later te vergelijken.",
    });
  }
  // D0 — baseline
  if (dayOffset === 0) {
    return PICK(locale, {
      fr: "Point zéro : ta référence visuelle et tes ressentis de départ.",
      en: "Zero point: your baseline visuals and how you feel today.",
      nl: "Nulpunt: jouw startbeelden en hoe je je vandaag voelt.",
    });
  }
  // D1 — same-day reaction (≤24h)
  if (dayOffset === 1) {
    return PICK(locale, {
      fr: "Réaction à 24 h : on documente l'état juste après le geste — rougeur, gonflement, sensations — pour servir de référence aux jours suivants.",
      en: "24-hour reaction: we document the state just after the procedure — redness, swelling, sensations — as a reference for the days that follow.",
      nl: "24-uurs reactie: we leggen vast hoe het er net na de behandeling uitziet — roodheid, zwelling, gevoel — als referentie voor de volgende dagen.",
    });
  }
  // D2–D3 — early post / inflammation peak
  if (dayOffset <= 3) {
    return PICK(locale, {
      fr: "Pic inflammatoire : c'est en général le moment le plus marqué. On vérifie que ça décroît et on écarte tout signe anormal (douleur croissante, suintement, fièvre).",
      en: "Inflammation peak: usually the most visible point. We check that it's already easing off and rule out anything abnormal (rising pain, oozing, fever).",
      nl: "Ontstekingspiek: meestal het meest uitgesproken moment. We controleren of het al afneemt en sluiten alarmsignalen uit (toenemende pijn, vocht, koorts).",
    });
  }
  // D4–D10 — early recovery / first visible signs
  if (dayOffset <= 10) {
    return PICK(locale, {
      fr: "Phase précoce : la peau (ou la zone) commence à se stabiliser, premiers indices visibles.",
      en: "Early phase: the area starts to settle and the first visible cues appear.",
      nl: "Vroege fase: de zone begint te stabiliseren, eerste zichtbare signalen.",
    });
  }
  // D11–D21 — first measurable change
  if (dayOffset <= 21) {
    return PICK(locale, {
      fr: "Premier bilan mesurable : on commence à voir des écarts objectifs vs S0.",
      en: "First measurable check-in: we start seeing objective gaps vs W0.",
      nl: "Eerste meetbare check-in: objectieve verschillen vs W0 worden zichtbaar.",
    });
  }
  // D22–D45 — mid-protocol / one-month review
  if (dayOffset <= 45) {
    return PICK(locale, {
      fr: "Bilan à 1 mois : on ajuste les habitudes selon l'évolution réelle, pas l'impression.",
      en: "One-month review: habits get tuned to actual evolution, not just impressions.",
      nl: "1-maand review: gewoonten bijstellen op basis van echte evolutie, niet alleen gevoel.",
    });
  }
  // D46–D75 — mid-term consolidation
  if (dayOffset <= 75) {
    return PICK(locale, {
      fr: "Mi-parcours : consolidation des bons réflexes et premiers gains durables.",
      en: "Mid-journey: locking in the good habits and first durable gains.",
      nl: "Halverwege: goede gewoonten verankeren, eerste duurzame winst.",
    });
  }
  // D76–D100 — late-stage results
  if (dayOffset <= 100) {
    return PICK(locale, {
      fr: "Phase de résultats : l'effort des semaines précédentes devient visible et stable.",
      en: "Results phase: the past weeks' effort becomes visible and stable.",
      nl: "Resultatenfase: de inspanning van weken wordt zichtbaar en stabiel.",
    });
  }
  // D101–D200 — quarterly / multi-month
  if (dayOffset <= 200) {
    return PICK(locale, {
      fr: "Bilan trimestriel : ce qui a tenu dans la durée vs ce qu'il faut relancer.",
      en: "Quarterly review: what stuck over time vs what to restart.",
      nl: "Kwartaalreview: wat bleef hangen vs wat opnieuw moet.",
    });
  }
  // > D200 — semester / long term
  return PICK(locale, {
    fr: "Bilan long terme : ancrage des changements et rapport partageable.",
    en: "Long-term review: changes anchored and shareable report.",
    nl: "Lange-termijn review: veranderingen verankerd en deelbaar rapport.",
  });
}

function PICK(locale: Locale, t: { fr: string; en: string; nl: string }): string {
  return t[locale] ?? t.fr;
}